import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TreeItem, TreeLevel } from '../../tree.model';
import { TreeComponent } from '../../tree/tree.component';
import { ContentSerializerService } from '../content-serializer.service';
import { ExpandPageHeightService, ExpandPageHeightInfo } from '../expand-page-height.service';

@Component({
  selector: 'app-content-canvas',
  standalone: true,
  imports: [CommonModule, TreeComponent],
  templateUrl: './content-canvas.component.html',
  styleUrl: './content-canvas.component.scss'
})
export class ContentCanvasComponent {
  @Input() selectedRoute: TreeItem | null = null;
  @Input() selectedRouteDisplayTitle = '';
  @Input() selectedComponent: TreeItem | null = null;
  @Input() selectedComponentDisplayTitle = '';
  @Input() editorChildItems: TreeItem[] = [];
  @Input() editorLevel = 0;
  @Input() isEditingEnabled = true;
  @Input() isDualPane = false;
  @Input() searchTerm = '';

  @Output() routePropertiesOpen = new EventEmitter<MouseEvent>();
  @Output() componentPropertiesOpen = new EventEmitter<MouseEvent>();
  @Output() orderChanged = new EventEmitter<void>();

  constructor(
    private serializer: ContentSerializerService,
    private pageHeightService: ExpandPageHeightService
  ) {}

  onTreeNameChanged() {
    if (!this.selectedRoute || !this.selectedComponent) return;
    const firstComponent = this.selectedRoute.children?.find(c => c.level === TreeLevel.Component);
    if (firstComponent === this.selectedComponent) {
      this.selectedRoute.name = this.selectedComponent.name;
    }
  }

  get isExpandRoute(): boolean {
    const routePath = (this.selectedRoute?.item as any)?.route ?? this.selectedRoute?.tag;
    return routePath === '/expand';
  }

  private lastPagesSignature = '';
  private lastPageHeightMap: Map<string, ExpandPageHeightInfo> | null = null;

  // Re-evaluated on every change-detection pass so edits show up immediately.
  // The tree-item signature keeps the frequent unchanged passes cheap; pages
  // are only serialized and re-measured when their content actually changes.
  get pageHeightMap(): Map<string, ExpandPageHeightInfo> | null {
    if (!this.isExpandRoute || !this.selectedComponent) return null;

    const pages = (this.selectedComponent.children || []).filter(child =>
      !child.isDeleted && child.level === TreeLevel.SubComponent && child.tag === 'page'
    );

    const signature = this.selectedComponent.id + '|' + pages.map(page =>
      page.id + ':' + (page.children || [])
        .filter(el => !el.isDeleted)
        .map(el => `${el.tag}=${el.updatedText ?? el.name ?? ''}`)
        .join('\u0001')
    ).join('\u0002');

    if (signature !== this.lastPagesSignature || !this.lastPageHeightMap) {
      this.lastPagesSignature = signature;
      const map = new Map<string, ExpandPageHeightInfo>();
      pages.forEach((page, index) => {
        const serialized = this.serializer.serializePage(page, this.selectedComponent!.id, index);
        map.set(page.id, this.pageHeightService.measureInfo(serialized));
      });
      this.lastPageHeightMap = map;
    }

    return this.lastPageHeightMap;
  }
}
